/**
 * Build a reverse mapping for regular questions based on the survey definition.
 * Each question is tagged with its dimension index, factor index, and its local index within the factor.
 *
 * @param {Object} surveyDefinition - The regular survey definition.
 * @returns {Object} An object mapping overall ques_order (number) to { dimIndex, factorIndex, localIndex, text, factor }.
 */
function buildReverseMapping(surveyDefinition) {
    const mapping = {};
    let overall = 1; // Assuming responses use 1-indexed ques_order
    surveyDefinition.dimensions.forEach((dimension, dimIndex) => {
      dimension.factors.forEach((factor, factorIndex) => {
        factor.questions.forEach((question, localIndex) => {
          mapping[overall] = {
            dimIndex,
            factorIndex,
            localIndex,
            text: question.q,      // the English text
            factor: factor.title   // the factor name
          };
          overall++;
        });
      });
    });
    return mapping;
  }
  
  /**
   * Build a mapping from the outcome survey definition.
   * Instead of using the question text as a key, we now key by "q-s" (e.g. "3-1").
   *
   * @param {Object} outcomeQ - The outcome survey definition.
   * @returns {Object} Mapping from outcome key (q-s) to { text, factor }.
   */
  function buildOutcomeMapping(outcomeQ) {
    const mapping = {};
    outcomeQ.questions.forEach((group) => {
      group.questions.forEach((question) => {
        // Build a key using q and s values (convert to string if needed)
        const key = `${question.q}-${question.s}`;
        mapping[key] = {
          text: question.question,  // English text
          factor: group.name        // use the group name as the factor name
        };
      });
    });
    return mapping;
  }
  
  /**
   * Calculates average scores for questions based on the dropdown selection.
   *
   * @param {Array} latestData - Array of response objects.
   *        Each response may include:
   *          - "questions": an array of regular question responses.
   *          - "employee_outcomes": an object with a "responses" array.
   * @param {string|Object} selectedFactor - Either a string ("Highest" or "Lowest")
   *        or an object. For regular questions, the object should be like:
   *          { id, title, dimension, type:'factor' }
   *        For outcome questions, the object should be like:
   *          { id, name, questions:[…], type:'outcome' }
   * @param {Object} surveyDefinition - The regular survey definition.
   * @param {Object} outcomeQ - The outcome survey definition.
   * @param {boolean} includeOutcomes - If true and the selection is "Highest"/"Lowest",
   *        outcome responses will be combined with regular responses.
   *
   * @returns {Array} Array of objects in the form { question, average, factorName }.
   */
  export function calculateAverageScores(latestData, selectedFactor, surveyDefinition, outcomeQ, includeOutcomes) {
    // Build mappings for regular and outcome questions.
    const reverseMapping = buildReverseMapping(surveyDefinition);
    const outcomeMapping = outcomeQ ? buildOutcomeMapping(outcomeQ) : {};
  
    // Objects for aggregation.
    const totals = {};   // groupKey -> cumulative score
    const counts = {};   // groupKey -> number of responses
    const details = {};  // groupKey -> { text, factor }
  
    // For outcome filtering when a specific outcome factor is selected.
    let allowedOutcomeQuestions = null;
    if (typeof selectedFactor === 'object' && selectedFactor.type === 'outcome') {
      // For outcome selections, build a set of allowed keys (q-s) from the selected factor’s questions.
      allowedOutcomeQuestions = new Set(
        selectedFactor.questions.map(q => `${q.q}-${q.s}`)
      );
    }
  
    // Determine which sources to include:
    // - For a regular factor selection (type 'factor'), include only regular responses.
    // - For an outcome factor selection (type 'outcome'), include only outcome responses.
    // - For a string selection ("Highest"/"Lowest"):
    //      • If includeOutcomes is true, include both.
    //      • Otherwise, include only regular responses.
    const includeNormal = (typeof selectedFactor !== 'object' || (typeof selectedFactor === 'object' && selectedFactor.type !== 'outcome'));
    const includeOutcome = (typeof selectedFactor === 'object' && selectedFactor.type === 'outcome') ||
                             (typeof selectedFactor === 'string' && includeOutcomes);
  
    // Process regular responses (found in response.questions).
    if (includeNormal) {
      latestData.forEach((response) => {
        if (!response.questions || !Array.isArray(response.questions)) return;
        response.questions.forEach((item) => {
          const info = reverseMapping[item.ques_order];
          if (!info) return; // Cannot map this question
  
          // If a specific regular factor is selected, only include if dimension and factor match.
          if (typeof selectedFactor === 'object' && selectedFactor.type !== 'outcome') {
            // selectedFactor.dimension is assumed to be set (e.g., 0 for People, 1 for Performance)
            // And selectedFactor.id is 1-indexed, so (selectedFactor.id - 1) gives the zero-based factor index.
            if (info.dimIndex !== selectedFactor.dimension || info.factorIndex !== (selectedFactor.id - 1)) {
              return; // skip this response
            }
          }
          // Build a grouping key based on dimension, factor, and the local index (position within the factor).
          const groupKey = `normal_${info.dimIndex}_${info.factorIndex}_${info.localIndex}`;
          totals[groupKey] = (totals[groupKey] || 0) + item.response;
          counts[groupKey] = (counts[groupKey] || 0) + 1;
          // Store details from the reverse mapping.
          details[groupKey] = { text: info.text, factor: info.factor };
        });
      });
    }
  
    // Process outcome responses (found in response.employee_outcomes.responses).
    if (includeOutcome) {
      latestData.forEach((response) => {
        if (response.employee_outcomes && Array.isArray(response.employee_outcomes.responses)) {
          response.employee_outcomes.responses.forEach((item) => {
            // For outcome responses, use q and s values to build the key.
            const outcomeKey = `${item.q}-${item.s}`;
            // If a specific outcome factor is selected, filter by allowed outcome keys.
            if (typeof selectedFactor === 'object' && selectedFactor.type === 'outcome') {
              if (!allowedOutcomeQuestions.has(outcomeKey)) {
                return;
              }
            }
            const groupKey = "outcome_" + outcomeKey;
            totals[groupKey] = (totals[groupKey] || 0) + item.response;
            counts[groupKey] = (counts[groupKey] || 0) + 1;
          });
        }
      });
    }
  
    // Convert aggregated totals into an array of average results.
    let averages = Object.keys(totals).map((groupKey) => {
      const avg = totals[groupKey] / counts[groupKey];
      let questionText = "";
      let factorName = "";
      if (groupKey.startsWith("normal_")) {
        // Look up details from our "details" object.
        const info = details[groupKey];
        if (info) {
          questionText = info.text;
          factorName = info.factor;
        } else {
          questionText = groupKey;
          factorName = "Unknown";
        }
      } else if (groupKey.startsWith("outcome_")) {
        // For outcome responses, use the outcomeMapping with key "q-s".
        const key = groupKey.replace("outcome_", "");
        const mapping = outcomeMapping[key];
        if (mapping) {
          questionText = mapping.text;
          factorName = mapping.factor;
        } else {
          questionText = key;
          factorName = "Unknown";
        }
      }
      return {
        question: questionText,
        average: avg,
        factorName: factorName,
        groupKey: groupKey // For debugging if needed
      };
    });
  
    // If the selection is a string ("Highest" or "Lowest"), sort and return only the top or bottom five.
    if (typeof selectedFactor === 'string') {
      if (selectedFactor === 'Highest') {
        averages = averages.sort((a, b) => b.average - a.average).slice(0, 5);
      } else if (selectedFactor === 'Lowest') {
        averages = averages.sort((a, b) => a.average - b.average).slice(0, 5);
      }
      return averages;
    }
  
    // For a specific factor selection (regular or outcome), return the grouped averages.
    return averages;
  }